import {
  Box,
  Typography,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material";
import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import WoodSupplierTaskService from "services/WoodSupplierTaskService";
import { styled } from "@mui/material/styles";
import ArrowBack from "assets/ArrowBack.svg";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

//Custom Image Text Box for displaying Images in text box
const ImageTextBox = ({ imageObject, label, height }) => {
  return (
    <TextField
      label={label}
      fullWidth
      sx={{
        height: `${height} !important`,
        "& .MuiOutlinedInput-root": {
          borderRadius: "3px !important",
          padding: "0 14px",
          "& input": {
            padding: "10px 0 !important", // vertically center text
            color: "#131619 !important",
          },
          "& fieldset": {
            border: "1px solid #C4C4C4", // default border
          },
          "&:hover fieldset": {
            border: "1px solid #0e2b63", // hover border
          },
          "&.Mui-focused fieldset": {
            border: "1px solid #0e2b63", // focused border
          },
        },
        "& .MuiInputLabel-root": {
          color: "rgba(0, 0, 0, 0.6)", // label color 60% black
          fontSize: "18px",
          background:
            "linear-gradient(to bottom, #EFEFEF 0%, #EFEFEF 48%, #fff 48%, #fff 100%)",
          paddingRight: "10px",
          fontWeight: "500",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          border: "1px solid #C4C4C4 !important",
        },
      }}
      InputProps={{
        readOnly: true,
        sx: { height: height },
        startAdornment: <InputAdornment position="start"></InputAdornment>,
        inputComponent: () => (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px !important",
            }}
          >
            {imageObject && (
              <Box
                component="img"
                src={"data:image/jpeg;base64," + imageObject.fileContent}
                alt={imageObject.fileName}
                sx={{
                  maxHeight: "90%",
                  maxWidth: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        ),
      }}
    />
  );
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    height: "46px",
    borderRadius: "3px !important",
    padding: "0 14px",
    "& input": {
      padding: "10px 0 !important", // vertically center text
    },
    "& fieldset": {
      border: "1px solid #C4C4C4", // default border
    },
    "&:hover fieldset": {
      border: "1px solid #C4C4C4", // hover border
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #C4C4C4", // focused border
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(0, 0, 0, 0.6) !important", // label color 60% black
    fontSize: "18px",
    background:
      "linear-gradient(to bottom, #EFEFEF 0%, #EFEFEF 48%, #fff 48%, #fff 100%)",
    paddingRight: "10px",
    fontWeight: "500",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #C4C4C4 !important",
  },
}));

const SignaturePage = forwardRef(
  ({ data, pdfLabels, onBack, onSignatureLoad }, ref) => {
    dayjs.extend(utc);
    const { t } = useTranslation();
    const innerBoxRef = useRef(null);
    const [values, setValues] = useState(data || {});
    const user = JSON.parse(sessionStorage.getItem("BAT_USER"));
    const [isGlobalProfile, setisGlobalProfile] = useState(
      user?.userAttribute == "Global" ? true : false
    );
    const [imageUrls, setImageUrls] = useState({
      picURL: null,
      farmerSignature: null,
      ftSignature: null,
      personSignature: null,
    });

    useEffect(()=>{
      setValues(data)
    },[data])

    useImperativeHandle(ref, () => ({
      element: innerBoxRef.current,
    }));

    const toBase64 = (str) => {
      return btoa(
        new TextEncoder()
          .encode(str)
          .reduce((data, byte) => data + String.fromCharCode(byte), "")
      );
    };

    const getImageUrls = async () => {
      const urlNames = [
        "ftSignature",
        "personSignature",
        // "ftFingerprint",
        "farmerSignature",
      ];
      !isGlobalProfile && urlNames.unshift("picURL");
      for (const url of urlNames) {
        if (!values[url]) continue;

        await WoodSupplierTaskService.getImageUrl(toBase64(values[url]))
          .then((res) => {
            const response = res.data.response;
            setImageUrls((prev) => ({
              ...prev,
              [url]: response,
            }));
          })
          .catch((e) => {
            console.error("Failed to fetch image:", e);
          });
      }
    };

    useEffect(() => {
      if (values) {
        (async () => {
          await getImageUrls();
          if (data) {
            onSignatureLoad?.(true);
          }
        })();
      }
    }, [values, data, onSignatureLoad]);

    // Forward the ref to the inner box
    useEffect(() => {
      if (ref) {
        ref.current = {
          element: innerBoxRef.current,
        };
      }
    }, [ref]);

    // Notify parent when signatures are loaded
    useEffect(() => {
      if (imageUrls.farmerSignature && imageUrls.ftSignature) {
        onSignatureLoad?.(true); // Notify parent component
      }
    }, [imageUrls, onSignatureLoad]);

    const DateField = () => {
      const isVisitCompliance = true;
      const date = isVisitCompliance
        ? values?.surveyDate
        : values?.surveyCompletitionDate;
      return (
        <StyledTextField
          label={pdfLabels?.wsdateSurvey || t("signaturePage.wsdateSurvey")}
          fullWidth
          value={date ? dayjs.utc(date).format("DD/MM/YYYY - HH:mm:ss") : ""}
          inputProps={{
            readOnly: true,
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
      );
    };

    return (
      <Box sx={{ pb: 4 }}>
        <img
          src={ArrowBack}
          alt="Arrow Back"
          style={{ marginBottom: "-20px", cursor: "pointer" }}
          height="16px"
          onClick={onBack}
        />
        <Box ref={innerBoxRef}>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 600,
              fontSize: "16px",
              color: "#102A62",
              mb: 1,
              ml: 3,
            }}
          >
            {pdfLabels?.signature_page || t("signaturePage.title")}
          </Typography>
          <Box sx={{ width: "98%", bgcolor: "#EFEFEF", padding: 2 }}>
            <Grid container spacing={2}>
              <Grid
                container
                item
                xs={isGlobalProfile ? 12 : 5.999}
                spacing={2}
              >
                <Grid
                  item
                  xs={isGlobalProfile ? 12 : 5.999}
                  display={isGlobalProfile && "none"}
                >
                  <StyledTextField
                    label={pdfLabels?.wsfarmerName || t("signaturePage.wsfarmerName")}
                    variant="outlined"
                    fullWidth
                    value={!values?.age ? values?.personName : ""}
                    inputProps={{
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={isGlobalProfile ? 12 : 5.999}>
                  <StyledTextField
                    label={pdfLabels?.wsgrowerNumber || t("signaturePage.wsgrowerNumber")}
                    fullWidth
                    value={values?.permanentCode}
                    variant="outlined"
                    inputProps={{
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                {!isGlobalProfile && (
                  <>
                    <Grid item xs={12}>
                      <StyledTextField
                        label={pdfLabels?.wsotherPersonName || t("signaturePage.wsotherPersonName")}
                        fullWidth
                        value={values?.age ? values?.personName : ""}
                        inputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        label={pdfLabels?.wsotherPersonAge || t("signaturePage.wsotherPersonAge")}
                        fullWidth
                        value={values?.age || ""}
                        inputProps={{
                          readOnly: true,
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DateField />
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid
                container
                item
                xs={5.999}
                spacing={2}
                display={isGlobalProfile && "none"}
              >
                <Grid item xs={12}>
                  <ImageTextBox
                    imageObject={imageUrls.picURL}
                    label={pdfLabels?.wsphoto || t("signaturePage.wsphoto")}
                    height="220px"
                  />
                </Grid>
              </Grid>
              <Grid container item xs={5.999} spacing={2}>
                {isGlobalProfile && (
                  <>
                    <Grid item xs={12}>
                      <DateField />
                    </Grid>
                    <Grid item xs={12}>
                      <ImageTextBox
                        imageObject={imageUrls.ftSignature}
                        label={pdfLabels?.wswmSignature || t("signaturePage.wswmSignature")}
                        height="112px"
                      />
                    </Grid>
                  </>
                )}
                {!isGlobalProfile && (
                  <Grid item xs={12}>
                    <StyledTextField
                      label={pdfLabels?.wsgeoCoordinate || t("signaturePage.wsgeoCoordinate")}
                      fullWidth
                      value={
                        (values?.gpsXCoordinates || 0) +
                        ", " +
                        (values?.gpsYCoordinates || 0)
                      }
                      inputProps={{
                        readOnly: true,
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} display={isGlobalProfile && "none"}>
                  <StyledTextField
                    label={pdfLabels?.wscomments || t("signaturePage.wscomments")}
                    fullWidth
                    value={values?.comment || ""}
                    inputProps={{
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={5.999} spacing={2}>
                {isGlobalProfile && (
                  <Grid item xs={12}>
                    <StyledTextField
                      label={pdfLabels?.wsgeoCoordinate || t("signaturePage.wsgeoCoordinate")}
                      fullWidth
                      value={
                        (values?.gpsXCoordinates || 0) +
                        ", " +
                        (values?.gpsYCoordinates || 0)
                      }
                      inputProps={{
                        readOnly: true,
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <ImageTextBox
                    imageObject={
                      imageUrls.farmerSignature
                        ? imageUrls.farmerSignature
                        : imageUrls.personSignature
                    }
                    label={pdfLabels?.wssignature || t("signaturePage.wssignature")}
                    height="112px"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {!isGlobalProfile && (
            <Box
              sx={{
                width: "98%",
                bgcolor: "#EFEFEF",
                padding: 2,
                mt: 2,
                mb: 10,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={5.999}>
                  <TextField
                    label={pdfLabels?.wswmName || t("signaturePage.wswmName")}
                    fullWidth
                    value={values?.ftName || ""}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "112px !important",
                        borderRadius: "3px !important",
                        padding: "0 14px",
                        "& input": {
                          padding: "10px 0 !important", // vertically center text
                        },
                        "& fieldset": {
                          border: "1px solid #C4C4C4", // default border
                        },
                        "&:hover fieldset": {
                          border: "1px solid #0e2b63", // hover border
                        },
                        "&.Mui-focused fieldset": {
                          border: "1px solid #0e2b63", // focused border
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(0, 0, 0, 0.6)", // label color 60% black
                        fontSize: "18px",
                        background:
                          "linear-gradient(to bottom, #EFEFEF 0%, #EFEFEF 48%, #fff 48%, #fff 100%)",
                        paddingRight: "10px",
                        fontWeight: "500",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "1px solid #C4C4C4 !important",
                      },
                    }}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        alignItems: "flex-start", // Align text to the top
                        height: "112px",
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={5.999}>
                  <ImageTextBox
                    imageObject={imageUrls.ftSignature}
                    label={pdfLabels?.wswmSignature || t("signaturePage.wswmSignature")}
                    height="112px"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);

SignaturePage.displayName = "SignaturePage";

export default memo(SignaturePage);
